import type { PropsWithChildren, MouseEvent, CSSProperties, ReactNode, MutableRefObject, RefObject } from "react";
import { TFormSize } from "./TFormSize";
import TooltipWrapper from "../common/TooltipWrapper";

export interface IButton {
  onClick?: (e?: MouseEvent<HTMLButtonElement>) => void,
  size?: TFormSize,
  variant?: 'outline' | 'filled',
  type?: 'button' | 'reset' | 'submit' | 'delete' | 'primary' | 'secondary',
  fontType?: 'thin' | 'medium' | 'bold',
  disabled?: boolean,
  className?: string,
  style?: CSSProperties,
  isNotFullWidth?: boolean,
  icon?: ReactNode,  
  btnRef?: MutableRefObject<HTMLInputElement | null | undefined>,
  toolTip?: string,
  isLoading?: boolean,
}

export default function Button ({  
  children,
  onClick,
  size = 'medium',
  variant = 'filled',
  type = 'button',
  fontType = 'medium',
  disabled = false,
  className,
  style,
  isNotFullWidth = false,
  btnRef,
  toolTip,
  isLoading,
}: PropsWithChildren<IButton>) {
  // const loadingColor = variant === 'filled'
  //   ? "white"
  //   : ""


  const button = <button
      {...(onClick ? { onClick } : {})}
      {...(style ? { style } : {})}
      {...(btnRef ? { ref: btnRef as RefObject<HTMLButtonElement> }: {})}
      disabled={disabled}
      className={`
        border-[1px] transition duration-300 py-2 px-4 rounded-lg disabled:opacity-50 tracking-wide flex gap-2 justify-center items-center      
        ${!!!isNotFullWidth && 'w-full'} 
        ${{
          'tiny':   'text-xs py-[3px] px-[7px] [&>svg]:h-[14px]',
          'small':  'text-sm py-[.6em] px-2 [&>svg]:h-[16px]  font-bold',
          'medium': 'py-3 px-5 [&>svg]:h-[20px]',
          'large':  'text-md py-3 px-4 [&>svg]:h-[22px]',
        }?.[size]}
        ${{
          'thin':   'font-thin',
          'medium': 'font-normal',
          'bold':   'font-bold',
        }?.[fontType]}
        
        ${{
          'primary' : {
            'outline': 'bg-white shadow-sm border-primary border-2  hover:text-primary_hover hover:border-primary_hover  text-primary hover:shadow-md',
            'filled': 'bg-primary border-primary hover:bg-primary_hover text-white shadow-sm hover:shadow-lg',
          },
          'secondary' : {
            'outline': 'bg-white shadow-sm border-secondary border-2  hover:text-secondary_hover hover:border-secondary_hover  text-secondary hover:shadow-md',
            'filled': 'bg-secondary border-secondary hover:bg-secondary_hover text-white shadow-sm hover:shadow-lg',
          },
          'submit' : {
            'outline': 'bg-white shadow-sm border-primary border-2  hover:text-secondary hover:border-secondary  text-primary hover:shadow-md',
            'filled': 'bg-primary border-primary hover:bg-primary_hover text-white shadow-sm hover:shadow-lg',
          },
          'reset' : {
            'outline': 'bg-white shadow-sm border-border_color hover:text-text_hard text-text hover:shadow-md',
            'filled': 'bg-primary border-primary_dark hover:bg-primary_hover hover:border-primary text-white shadow-sm hover:shadow-lg',
          },
          'button' : {
            'outline': 'bg-white shadow-sm border-border_color hover:text-text_hard text-text hover:shadow-md',
            'filled': 'bg-white hover:bg-gray-300 hover:border-gray-300 text-black shadow-sm hover:shadow-lg',
          },
          'delete' : {
            'outline': 'bg-white shadow-sm border-border_color hover:border-red-600 hover:text-red-600 text-red-700 hover:shadow-md',
            'filled': ' bg-red-600 hover:bg-red-600 hover:border-red-700 text-white shadow-sm hover:shadow-lg',
          },
        }?.[type]?.[variant]}
        ${isLoading
          ? `[&_svg_circle]:stroke-white [&_svg_circle]:fill-white [&>svg]:h-[22px]`
          : ''
        }
        ${className ? className : ''}
      `}>
      {isLoading
        ? <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200"><circle fill="#000000" stroke="#000000" strokeWidth="21" r="15" cx="40" cy="65"><animate attributeName="cy" calcMode="spline" dur="2" values="65;135;65;" keySplines=".5 0 .5 1;.5 0 .5 1" repeatCount="indefinite" begin="-.4"></animate></circle><circle fill="#000000" stroke="#000000" strokeWidth="21" r="15" cx="100" cy="65"><animate attributeName="cy" calcMode="spline" dur="2" values="65;135;65;" keySplines=".5 0 .5 1;.5 0 .5 1" repeatCount="indefinite" begin="-.2"></animate></circle><circle fill="#000000" stroke="#000000" strokeWidth="21" r="15" cx="160" cy="65"><animate attributeName="cy" calcMode="spline" dur="2" values="65;135;65;" keySplines=".5 0 .5 1;.5 0 .5 1" repeatCount="indefinite" begin="0"></animate></circle></svg>
        : children
      }
    </button>;
  
  return toolTip
    ? <TooltipWrapper content={toolTip}>{button}</TooltipWrapper>
    : button;  
}